// Record pages: the deal page on RecordPage (record-page redesign step 10;
// docs/record-page-redesign.md "### Engagement · seller deal" / "buyer pursuit shell").
// DealDetail (views/deal_detail.jsx) keeps every fetch, save path and action-drawer
// state and renders <window.DealPageV2 …/> on the v2 surface; this file owns the
// v2-only hooks (summary, panel param, draft open, contacts, team members) and the
// layout. Cross-file names are read from window at render time.

const DP_DIM = { color: 'var(--text-4)' };
const DP_LABEL_W = 110;
const DP_HOLD_OPTIONS = [
  { value: null, label: 'Active' },
  { value: 'high', label: 'On hold · high', hue: 'amber' },
  { value: 'low', label: 'On hold · low', hue: 'amber' },
  { value: 'repricing', label: 'On hold · repricing', hue: 'amber' },
];
// Delay type options from deal_detail.jsx's HOLD_REASON_LABEL (exported by this step;
// read at render so the labels stay one table: 'Owner delay' / 'Business delay').
const dpDelayOptions = (labels) => Object.keys(labels || {}).map((value) => ({ value, label: labels[value], hue: 'amber' }));
const DP_PEOPLE_SHOWN = 5;
const DP_FILES_SHOWN = 3;

// ── DealPageKit (pure; atlas-crm/test/deal_page_v2.test.mjs) ──────────────────
const dpVariant = (deal) => (deal && deal.type === 'buyer_buyside' ? 'shell' : 'seller');
// primary first → decision-makers → last touch desc → last name (server _primary_contact_order).
const dpRankPeople = (contacts, primaryId) => {
  const score = (p) => (String(p.id) === String(primaryId || '') ? 4 : 0) + (p.is_decision_maker ? 2 : 0);
  const last = (p) => String(p.last_name || p.full_name || '').toLowerCase();
  return [...(contacts || [])].sort((a, b) => (score(b) - score(a))
    || String(b.last_interaction_at || '').localeCompare(String(a.last_interaction_at || ''))
    || last(a).localeCompare(last(b)));
};
// Header pills in priority order (null entries dropped by HeaderPills); `acq` is the
// shared fragment (an element or null). Seller: meeting · acquisition · hold · type.
// Shell: type (Buyer) · pursuit pill · meeting. Returns the ordered keys; the page maps
// them to elements, so the order is testable without React.
const dpPillKeys = (deal, { liveMeeting = false, hasAcq = false } = {}) => {
  if (dpVariant(deal) === 'shell') return ['type', 'pursuit', liveMeeting ? 'meeting' : null].filter(Boolean);
  return [liveMeeting ? 'meeting' : null, hasAcq ? 'acquisition' : null, deal.onHold ? 'hold' : null, 'type'].filter(Boolean);
};
const dpHoldLabel = (deal, labels) => `On hold · ${deal.onHold}${deal.onHoldReason ? ` · ${(labels || {})[deal.onHoldReason] || deal.onHoldReason}` : ''}`;
// The compact chip = the banner's first word; null when no terminal state.
const dpTerminal = (deal) => {
  if (!deal) return null;
  if (deal.archived) return deal._raw && deal._raw.is_duplicate ? 'Duplicate' : 'Archived';
  if (deal.exitName) return 'Exited';
  return null;
};
// Archived first, as the chip (dpTerminal) does: an archived shell is Archived, not a pursuit.
const dpBannerText = (deal) => {
  if (!deal) return null;
  const raw = deal._raw || {};
  if (deal.archived) return raw.is_duplicate || deal.archivedReason === 'reconciled_duplicate' ? 'Archived · duplicate' : 'Archived';
  if (deal.type === 'buyer_buyside' && deal.sellerEngagementId && !deal.exitName) return 'Buyer pursuit';
  if (deal.exitName) return `Exited · ${deal.exitName}`;
  return null;
};
// (History tabs live in one place: record_panels.jsx anchor A exports window.RECORD_HISTORY_TABS
// and activity.jsx's RecordHistoryBody filters it per viewer — this kit has no copy.)
// A seller deal with no company has no vault; its Files card takes the vault's slot.
const dpCardIds = (variant, { guest = false, noCompany = false } = {}) => (variant === 'shell'
  ? ['deal.seller_deal', 'deal.meetings', 'deal.files']
  : guest
    ? ['deal.buyers', 'deal.seller_brief', 'deal.intel', 'deal.files', 'deal.meetings']
    : ['deal.buyers', 'deal.blast', 'deal.seller_brief', 'deal.intel', 'deal.comps', noCompany ? 'deal.files' : 'deal.vault', 'deal.meetings', 'deal.ers', 'deal.workspaces']);
const dpKeyFactsPreview = (deal, fmtMoney) => [deal.owner && deal.owner !== '—' ? deal.owner : null,
  deal.hotScore != null ? `Hot ${deal.hotScore}` : null, deal.size != null ? fmtMoney(deal.size) : null].filter(Boolean).join(' · ') || '—';
const dpMeetingsLine = (m, fmtDate) => (!m || !m.count ? null
  : [`${m.count} recorded`, m.avg_positive != null ? `avg positive ${m.avg_positive}%` : null, m.last && m.last.at ? `last ${fmtDate(m.last.at)}` : null].filter(Boolean).join(' · '));
const dpErsLine = (e) => (!e ? null : [`${Number(e.score_total || 0).toFixed(1)} / 10`, e.routing ? String(e.routing).replace(/_/g, ' ') : null, e.primary_blocker || null].filter(Boolean));
const dpExitPrimary = (deal, { stageExits = [], currentExit = null, companyId = null } = {}) => {
  if (!deal.exitName) return stageExits.length ? 'exit' : null;
  if (currentExit && currentExit.is_recoverable) return 'reopen';
  if (currentExit && currentExit.slug === 'company_sold' && companyId) return 'not_acquired';
  return null;
};
const DealPageKit = Object.freeze({
  variant: dpVariant, rankPeople: dpRankPeople, pillKeys: dpPillKeys, holdLabel: dpHoldLabel,
  terminal: dpTerminal, bannerText: dpBannerText, cardIds: dpCardIds, keyFactsPreview: dpKeyFactsPreview,
  meetingsLine: dpMeetingsLine, ersLine: dpErsLine, exitPrimary: dpExitPrimary, delayOptions: dpDelayOptions,
  PEOPLE_SHOWN: DP_PEOPLE_SHOWN, FILES_SHOWN: DP_FILES_SHOWN, HOLD_OPTIONS: DP_HOLD_OPTIONS,
});
window.DealPageKit = DealPageKit;

// ── building blocks ───────────────────────────────────────────────────────────
// One Key-facts row (the classic Row's look: 110px label, value flex, dimmed '—').
const KeyRow = ({ label, value, sub }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', minHeight: 30 }}>
    <span style={{ width: DP_LABEL_W, fontSize: 11.5, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {value == null || value === '' || value === '—' ? <span style={DP_DIM}>—</span> : value}
      {sub != null && sub !== '' && <span style={{ fontSize: 10.5, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{sub}</span>}
    </span>
  </div>
);
const KeyFooter = ({ onOpen }) => (
  <div style={{ padding: '6px 14px 10px' }}>
    <button type="button" data-all-details="" onClick={onOpen}
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 11.5, fontFamily: 'inherit' }}>All details ›</button>
  </div>
);
const DP_BANNER = {
  violet: { background: 'var(--violet-bg, var(--surface-2))', color: 'var(--violet-fg, var(--text-2))' },
  amber: { background: 'var(--amber-bg)', color: 'var(--amber-fg)' },
  rose: { background: 'var(--rose-bg)', color: 'var(--rose-fg)' },
};
// One line: icon · text · ≤2 link buttons · an optional trailing node (the exit note).
const BannerLine = ({ hue, icon, text, links = [], children }) => (
  <div data-record-banner-line={hue} style={{ ...DP_BANNER[hue], height: 24, padding: '0 10px', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, whiteSpace: 'nowrap' }}>
    {icon}
    <span style={{ flexShrink: 0 }}>{text}</span>
    {links.filter(Boolean).slice(0, 2).map((l) => (
      <button key={l.label} type="button" onClick={l.onClick}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', fontSize: 12, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>{l.label} →</button>
    ))}
    {children}
  </div>
);
const dpLink = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit', textUnderlineOffset: 2 };

// ── header pieces ─────────────────────────────────────────────────────────────
// The compact stage pill: the same pick PropertiesPanel's Stage row makes (Won → ClosingDrawer
// through requestStage, which carries its own confirm, so the cell is not DC-locked).
const DealStagePill = ({ deal, stageList, canWrite, onRequestStage }) => {
  const options = (stageList || []).map((s) => ({ value: s.id, label: s.name, hue: (window.STAGE_DOT_HUE || {})[s.slug] || 'slate' }));
  return (
    <window.EditableCell type="select" value={deal._raw && deal._raw.stage_id} options={options} editable={canWrite}
      displayAs={<window.Pill hue={(window.STAGE_DOT_HUE || {})[deal.stage] || 'slate'} dot>{deal.stageName || deal.stage}</window.Pill>}
      onSave={(stage_id) => { const i = (stageList || []).findIndex((s) => s.id === stage_id); if (i >= 0) onRequestStage(stageList[i], i); return Promise.resolve(); }} />
  );
};
// Shells: the pursuit picker (PURSUIT_STAGE_OPTIONS) — header pill #2 and the compact pill.
const PursuitPill = ({ deal, canWrite, onPursuitStage }) => (
  <window.EditableCell type="select" value={window.pursuitKeyFor(deal.dcStage)} options={window.PURSUIT_STAGE_OPTIONS} editable={canWrite}
    displayAs={deal.dcStage ? <window.Pill hue={window.dcStageHue(deal.dcStage)} dot>{window.cleanDcStage(deal.dcStage)}</window.Pill> : <window.Pill>—</window.Pill>}
    onSave={onPursuitStage} />
);
// The meta line: company · industry (no hook; RecordHeader renders LastTouch from the
// page's `lastTouch`, which the root summary read feeds — amendment A2).
const DealMetaLine = ({ deal, company, canOpenCompanies }) => {
  const name = (company && company.name) || deal.targetName || null;
  const parts = [];
  if (name) parts.push(canOpenCompanies && (company || deal.target)
    ? <button key="co" type="button" onClick={() => window.AtlasNav && window.AtlasNav.goToCompany((company && company.id) || deal.target)} style={{ ...dpLink, color: 'var(--text-3)' }}>{name}</button>
    : <span key="co">{name}</span>);
  if (deal.sector && deal.sector !== '—') parts.push(<span key="ind">{deal.sector}</span>);
  if (!parts.length) return null;
  return (
    <>
      {parts.map((p, i) => <React.Fragment key={i}>{i ? <span aria-hidden="true" style={DP_DIM}>·</span> : null}{p}</React.Fragment>)}
    </>
  );
};

// ── left: Key facts ───────────────────────────────────────────────────────────
const DealKeyFacts = ({ deal, company, stageList, users, canWrite, guest, onSave, shared }) => {
  const [askingHoldReason, setAskingHoldReason] = React.useState(false);
  const raw = deal._raw || {};
  const dcLocked = Boolean(raw.dealcloud_id);
  const shell = deal.type === 'buyer_buyside';
  const fmtMoney = window.fmtMoney; const fmtDate = window.fmtDate;
  const stage = (stageList || []).find((s) => s.slug === deal.stage);
  const coOwnerDiffers = !!(company && company._raw && company._raw.assigned_user_id && company._raw.assigned_user_id !== raw.dealmaker_id);
  const money = (label, field, fallback) => (
    <KeyRow key={field} label={label}
      value={<window.EditableCell type="currency" value={raw[field] ?? null} locked={dcLocked} editable={canWrite}
        displayAs={raw[field] == null && fallback != null ? <span className="num">{fmtMoney(fallback)}</span> : undefined}
        onSave={(v) => onSave({ [field]: v })} />}
      sub={raw[field] == null && fallback != null ? 'via company' : null} />
  );
  const signals = raw.signals || {};
  const reached = Object.keys(signals).some((k) => signals[k] && signals[k].reached);
  return (
    <window.Section variant="rail" title="Key facts" collapsible id="deal.key_facts" preview={DealPageKit.keyFactsPreview(deal, fmtMoney)}>
      <KeyRow label="Dealmaker" value={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <window.EditableCell type="user" value={raw.dealmaker_id || null} users={users} fallbackLabel={raw.dealmaker_name} locked={dcLocked} editable={canWrite}
            onSave={(dealmaker_id) => window.saveOwnerWithScope({ kind: 'engagement', field: 'dealmaker_id', value: dealmaker_id, users,
              companyName: (company && company.name) || deal.targetName, hasCompany: !!deal.target, save: onSave })} />
          {raw.owner_locked && <window.OwnerLockHint />}
        </span>} />
      {!shell && coOwnerDiffers && (
        <KeyRow label="Company owner" value={
          <window.EditableCell type="user" value={company._raw.assigned_user_id} users={users} fallbackLabel={company._raw.assigned_user_name} editable={canWrite}
            onSave={(assigned_user_id) => window.saveOwnerWithScope({ kind: 'company', field: 'assigned_user_id', value: assigned_user_id, users,
              companyName: company.name, save: (patch) => window.AtlasApi.updateCompany(company.id, patch).then((co) => { shared.onCompanyChange(co); return co; }) })} />} />
      )}
      {shell && <KeyRow label="Associate" value={<window.EditableCell type="user" anyone value={raw.associate_id || null} users={users} fallbackLabel={raw.associate_name} locked={dcLocked} editable={canWrite} onSave={(associate_id) => onSave({ associate_id })} />} />}
      <KeyRow label="Deal team" value={<window.EditableCell type="users" value={raw.deal_team || []} users={users} fallbackLabel={raw.deal_team_names || []} editable={canWrite} onSave={(deal_team) => onSave({ deal_team })} />} />
      <KeyRow label="Hot score" value={<window.HotScoreCell value={raw.hot_score ?? null} editable={canWrite} onSave={(hot_score) => onSave({ hot_score })} />} />
      {!shell && <KeyRow label="Est. fees" sub={deal.weighted != null ? `weighted ${fmtMoney(deal.weighted)}` : null}
        value={<window.EditableCell type="currency" value={deal.size} locked={dcLocked} editable={canWrite} onSave={(estimated_fees) => onSave({ estimated_fees })} />} />}
      {!shell && (
        <KeyRow label="LOI signed" value={
          <window.EditableCell type="date" label="LOI signed" value={raw.loi_signed_date || null} editable={canWrite}
            displayAs={raw.loi_signed_date == null && raw.loi_date ? <span className="num" style={{ color: 'var(--text-3)' }} title="From the LOI stage date">{fmtDate(raw.loi_date)}</span> : undefined}
            onSave={(loi_signed_date) => onSave({ loi_signed_date })} />} />
      )}
      <KeyRow label="Est. close" value={
        <window.EditableCell type="date" value={deal.estCloseDate} locked={dcLocked} editable={canWrite}
          displayAs={deal.estCloseDate ? (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 6, fontSize: 'inherit' }}>
              <window.EstCloseValue value={deal.estCloseDate} closed={window.dealIsClosed(deal, stage)} />
              {!shell && window.CloseDateHint && <span style={{ fontSize: 10.5, color: 'var(--text-4)', whiteSpace: 'nowrap' }}><window.CloseDateHint deal={deal} /></span>}
            </span>) : undefined}
          onSave={(estimated_close_date) => onSave({ estimated_close_date })} />} />
      {!shell && (
        <KeyRow label="Retainer" value={<window.RetainerCell deal={deal} canWrite={canWrite} onSave={onSave} />} />
      )}
      {!shell && money('Reported revenue', 'reported_revenue', deal.companyRevenueEst)}
      {!shell && money('Reported EBITDA', 'reported_ebitda', deal.companyEbitdaEst)}
      {!shell && (
        <KeyRow label="On hold" value={
          <window.EditableCell type="select" value={deal.onHold || null} options={DP_HOLD_OPTIONS} locked={dcLocked} editable={canWrite}
            onSave={async (on_hold) => { await onSave({ on_hold }); if (on_hold && (!deal.onHold || !deal.onHoldReason)) setAskingHoldReason(true); }} />} />
      )}
      {!shell && deal.onHold && (
        <KeyRow label="Delay type" value={
          <window.EditableCell type="select" value={deal.onHoldReason || null} options={DealPageKit.delayOptions(window.HOLD_REASON_LABEL)} editable={canWrite}
            onSave={(on_hold_reason) => onSave({ on_hold_reason })} />} />
      )}
      {!shell && deal.onHold && (
        <KeyRow label="Check-in" value={
          <window.EditableCell type="select" value={deal.holdCheckinCadence || null} editable={canWrite}
            options={(window.HOLD_CADENCE_OPTIONS || []).map((o) => ({ ...o, hue: o.value === 'none' ? 'slate' : 'blue' }))}
            onSave={(hold_checkin_cadence) => onSave({ hold_checkin_cadence: hold_checkin_cadence || 'none' })} />} />
      )}
      {guest ? (
        <>
          <KeyRow label="Lead source" value={raw.lead_source_name} />
          <KeyRow label="Event" value={raw.lead_event_name} />
        </>
      ) : <window.LeadSourceFields raw={deal._raw || {}} onSave={onSave} Row={KeyRow} />}
      {shell && <KeyRow label="Last activity" value={deal.lastActivityAt ? fmtDate(deal.lastActivityAt) : null} />}
      {!shell && reached && (
        <div style={{ padding: '6px 14px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--text-3)', marginBottom: 4 }}>
            Milestones
            <window.HelpTip text="A milestone is a stage the deal has evidence of reaching: a note, a call, a blast or a stage move." />
          </div>
          <window.SignalsChips signals={signals} />
        </div>
      )}
      <KeyFooter onOpen={() => window.openRecordPanel('details', {}, { via: 'button' })} />
      <window.HoldReasonPrompt open={askingHoldReason} reason={deal.onHoldReason} cadence={deal.holdCheckinCadence}
        onPick={async (patch) => { setAskingHoldReason(false); await onSave(patch); }}
        onClose={() => setAskingHoldReason(false)} />
    </window.Section>
  );
};

// ── left: People ──────────────────────────────────────────────────────────────
const PersonRow = ({ p, primary }) => {
  const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
  const go = () => window.AtlasNav && window.AtlasNav.goToContact(p.id);
  return (
    <div data-person-row={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', minWidth: 0 }}>
      <window.Avatar name={name} size={20} color="oklch(0.50 0.13 295)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <button type="button" onClick={go} title={name} style={{ ...dpLink, fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{name}</button>
          {primary && <window.Pill hue="teal" sz="sm">Deal contact</window.Pill>}
          {p.is_decision_maker && <window.Pill hue="violet" sz="sm">Decision-maker</window.Pill>}
          {p.do_not_call && <window.Pill hue="rose" sz="sm">DNC</window.Pill>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || <span style={DP_DIM}>—</span>}</div>
      </div>
      <window.CallButton variant="icon" phone={p.phone} doNotCall={!!p.do_not_call} name={name} />
      {p.email && (
        <a href={`mailto:${p.email}`} aria-label={`Email ${name}`} title={p.email}
          style={{ display: 'inline-flex', alignItems: 'center', padding: 2, color: 'var(--accent-text)', lineHeight: 0 }}><window.Icon.Mail size={12} /></a>
      )}
      {p.email && <window.CopyIconBtn text={p.email} />}
    </div>
  );
};
const PeopleCard = ({ deal, guest, canWrite, contacts, onAdd }) => {
  const raw = deal._raw || {};
  if (!deal.target) return null;
  const ranked = guest ? null : DealPageKit.rankPeople(contacts || [], raw.primary_contact_id);
  const n = guest ? (raw.primary_contact_name ? 1 : 0) : (contacts ? contacts.length : null);
  const canOpenAll = !guest && window.recordPanelAvailable('people', window.recordSurface());
  const action = guest ? null : (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {canWrite && window.NewPersonDrawer && <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onAdd}>Add</window.Btn>}
      {canOpenAll && <window.Btn size="xs" variant="ghost" onClick={() => window.openRecordPanel('people', {}, { via: 'button' })}>{n == null ? 'All —' : `All ${n}`}</window.Btn>}
    </span>
  );
  return (
    <window.Section variant="rail" title="People" collapsible id="deal.people" preview={n == null ? '—' : `${n} contact${n === 1 ? '' : 's'}`} action={action}>
      {guest ? (
        raw.primary_contact_name ? (
          <div style={{ padding: '8px 14px', fontSize: 12.5 }}>
            <span style={{ fontWeight: 500 }}>{raw.primary_contact_name}</span>
            {raw.primary_contact_title && <span style={{ color: 'var(--text-3)' }}>{' · '}{raw.primary_contact_title}</span>}
          </div>
        ) : <div style={{ padding: '8px 14px', ...DP_DIM }}>—</div>
      ) : contacts == null ? <div style={{ padding: '8px 14px', ...DP_DIM }}>—</div>
        : ranked.length === 0 ? <div style={{ padding: '8px 14px', ...DP_DIM }}>0</div>
        : ranked.slice(0, DP_PEOPLE_SHOWN).map((p) => <PersonRow key={p.id} p={p} primary={String(p.id) === String(raw.primary_contact_id || '')} />)}
    </window.Section>
  );
};

// ── right: one-line / short cards off the summary (each owns its hook) ────────
const oneLine = (onOpen, children) => (
  <button type="button" onClick={onOpen || undefined} style={{ ...dpLink, display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '8px 14px', textAlign: 'left', fontSize: 12, color: 'var(--text-2)', cursor: onOpen ? 'pointer' : 'default', minWidth: 0 }}>{children}</button>
);
const MeetingsCard = ({ deal }) => {
  const sum = window.useRecordSummary('deal', deal.id);
  const m = (sum.data && sum.data.meetings) || null;
  if (!m || !m.count) return null;
  const line = DealPageKit.meetingsLine(m, window.fmtDate);
  const open = () => window.openRecordPanel('calls', {}, { via: 'button' });
  return (
    <window.SummaryCard title="Meetings & calls" collapsible id="deal.meetings" preview={line}
      action={<window.Btn size="xs" variant="ghost" title="Open" icon={<window.Icon.Maximize size={11} />} onClick={open} />}>
      {oneLine(open, <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line}</span>)}
    </window.SummaryCard>
  );
};
const ErsLine = ({ deal }) => {
  const sum = window.useRecordSummary('deal', deal.id);
  const e = (sum.data && sum.data.ers) || null;
  if (!e) return null;
  const [score, routing, blocker] = DealPageKit.ersLine(e).concat([null, null]);
  const hue = e.routing === 'blast_ready' ? 'green' : e.routing === 'developing' ? 'amber' : e.routing === 'not_ready' ? 'rose' : 'slate';
  return (
    <window.SummaryCard title="Exit readiness" collapsible id="deal.ers" preview={DealPageKit.ersLine(e).join(' · ')}>
      {oneLine(null, <><span className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>{score}</span>{routing && <window.Pill hue={hue} dot sz="sm">{routing}</window.Pill>}{blocker && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{blocker}</span>}</>)}
    </window.SummaryCard>
  );
};
const WorkspacesCard = ({ deal }) => {
  const sum = window.useRecordSummary('deal', deal.id);
  const rows = (sum.data && sum.data.workspaces) || [];
  if (!rows.length) return null;
  const hue = (s) => (s === 'running' ? 'blue' : s === 'completed' ? 'green' : s === 'failed' ? 'rose' : 'slate');
  return (
    <window.SummaryCard title={`Agent workspaces · ${rows.length}`} collapsible id="deal.workspaces" preview={rows.map((w) => w.name).join(' · ')}>
      {rows.map((w) => {
        const status = w.latest_run_status || (w.active_run_count > 0 ? 'running' : w.status || 'idle');
        return (
          <React.Fragment key={w.id}>
            {oneLine(() => window.AtlasNav && window.AtlasNav.goToWorkspace(w.id), (
              <>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
                <window.Pill hue={hue(status)} dot sz="sm">{status}</window.Pill>
                <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{String(w.lead_agent_key || '').replace(/-/g, ' ')}</span>
              </>))}
          </React.Fragment>
        );
      })}
    </window.SummaryCard>
  );
};
// Guest deals and a seller deal with no company (always: no vault holds its files) and shells (when ≥1):
// the 3 latest, each through openAttachment; ⤢ → the Files workspace.
const FilesCard = ({ deal, always = false }) => {
  const sum = window.useRecordSummary('deal', deal.id);
  const f = (sum.data && sum.data.files) || null;
  if (!always && (!f || !f.count)) return null;
  const n = f ? f.count : null;
  const canOpen = window.recordPanelAvailable('files', window.recordSurface());
  const open = () => window.openRecordPanel('files', {}, { via: 'button' });
  return (
    <window.SummaryCard title={n == null ? 'Files · —' : `Files · ${n}`} collapsible id="deal.files" preview={n == null ? '—' : `${n} file${n === 1 ? '' : 's'}`}
      action={canOpen ? <window.Btn size="xs" variant="ghost" title="Open" icon={<window.Icon.Maximize size={11} />} onClick={open} /> : null}>
      {!f ? <div style={{ padding: '8px 14px', ...DP_DIM }}>—</div> : f.count === 0 ? <div style={{ padding: '8px 14px', ...DP_DIM }}>0</div>
        : (f.latest || []).slice(0, DP_FILES_SHOWN).map((a) => (
          <React.Fragment key={a.id}>
            {oneLine(() => window.AtlasApi.openAttachment(a.id, a.filename), (
              <>
                <span style={{ flex: 1, minWidth: 0, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename}</span>
                <span className="num" style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{a.at ? window.fmtDate(a.at) : ''}</span>
              </>))}
          </React.Fragment>
        ))}
    </window.SummaryCard>
  );
};
// Shells: the seller deal and this buyer's board row; 'Open on board' focuses this pursuit.
const SellerDealCard = ({ deal }) => {
  const sum = window.useRecordSummary('deal', deal.id);
  const sd = (sum.data && sum.data.seller_deal) || null;
  if (!sd) return null;
  const r = sd.board_row || null;
  const fmtDate = window.fmtDate;
  const go = (params) => window.AtlasNav && window.AtlasNav.goToDeal(sd.id, params);
  const chip = (label, v) => <span style={{ display: 'inline-flex', gap: 4, fontSize: 11.5 }}><span style={DP_DIM}>{label}</span><span>{v == null || v === '' ? <span style={DP_DIM}>—</span> : v}</span></span>;
  return (
    <window.SummaryCard title="Seller deal" collapsible id="deal.seller_deal" preview={[sd.name, sd.stage, sd.dealmaker].filter(Boolean).join(' · ')}
      action={<window.Btn size="xs" variant="outline" onClick={() => go({ panel: 'buyers', tab: 'board', focus: deal.id })}>Open on board</window.Btn>}>
      <div style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" onClick={() => go()} style={{ ...dpLink, textAlign: 'left', fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{sd.name}</button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-2)' }}>{sd.stage && <window.Pill sz="sm">{sd.stage}</window.Pill>}{sd.dealmaker && <span>{sd.dealmaker}</span>}</div>
        {r && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {chip('NDA', r.nda_status ? `${String(r.nda_status).replace(/_/g, ' ')}${r.nda_signed_date ? ` · ${fmtDate(r.nda_signed_date)}` : ''}` : null)}
            {chip('Interest', r.interest_level === 'passed' ? `passed${r.pass_reason ? ` · ${String(r.pass_reason).replace(/_/g, ' ')}` : ''}` : r.interest_level)}
            {chip('B/S call', r.bs_call_date ? fmtDate(r.bs_call_date) : null)}
            {chip('LOI', r.loi_submitted ? 'submitted' : null)}
          </div>
        )}
      </div>
    </window.SummaryCard>
  );
};

// ── the right column per variant ─────────────────────────────────────────────
const DealRight = ({ deal, variant, guest }) => {
  if (variant === 'shell') {
    return (<><SellerDealCard deal={deal} /><MeetingsCard deal={deal} /><FilesCard deal={deal} /></>);
  }
  return (
    <>
      <window.DealBuyersCard deal={deal} variant="rail" />
      {!guest && window.BlastNetworkCard && <window.BlastNetworkCard deal={deal} canWrite={window.recordCanWrite()} />}
      {window.LoiCompareCard && <window.LoiCompareCard deal={deal} variant="rail" />}
      <window.IntelCard deal={deal} variant="rail" />
      {!guest && <window.MarketCard deal={deal} variant="rail" />}
      {!guest && deal.target && window.CompanyVaultSection && (
        <window.CompanyVaultSection companyId={deal.target} sectionId="deal.vault" panelRecord={{ kind: 'deal', id: deal.id }}
          engagementId={!deal._raw.is_duplicate ? deal.id : null} />
      )}
      {(guest || !deal.target) && <FilesCard deal={deal} always />}
      <MeetingsCard deal={deal} />
      {!guest && <ErsLine deal={deal} />}
      {!guest && <WorkspacesCard deal={deal} />}
    </>
  );
};

// ── the ⋯ menu + the Merge drawer ────────────────────────────────────────────
const DealMergeDrawer = ({ deal, onClose }) => (
  <window.Drawer open title="Merge duplicate" width={440} widthKey="dealMerge" onClose={onClose}>
    <window.DealMergeRow deal={deal} initialOpen />
  </window.Drawer>
);
const dealMenuItems = ({ deal, shell, isPinned, onTogglePin, drawers, onSave, setMerging }) => {
  const raw = deal._raw || {};
  const dup = !!raw.is_duplicate;
  const items = [
    { key: 'pin', label: isPinned ? 'Unpin' : 'Pin', disabled: isPinned === null, onSelect: onTogglePin },
    { key: 'ask', label: 'Ask about this deal', onSelect: () => window.dispatchEvent(new CustomEvent('ask-atlas', { detail: { query: '' } })) },
    { key: 'history', label: 'History', onSelect: () => window.openRecordPanel('history', {}, { via: 'button' }) },
    !shell && !dup && { key: 'cim', label: 'CIM Command Center', onSelect: () => window.AtlasNav && window.AtlasNav.goToCim(deal.id) },
    !shell && { key: 'workspace', label: 'New agent workspace', onSelect: drawers.onWorkspaceOpen },
    { divider: true },
    // DealMergeRow itself renders nothing for a company-less or duplicate deal (classic
    // shows nothing there either); the gate only keeps the drawer from opening empty.
    !dup && !!raw.company_id && { key: 'merge', label: 'Merge another deal into this one…', onSelect: () => setMerging(true) },
    deal.archived
      ? { key: 'unarchive', label: 'Un-archive', onSelect: () => onSave({ is_archived: false, archived_reason: null }) }
      : { key: 'archive', label: 'Archive…', onSelect: async () => {
          const ok = await window.showConfirm({ title: `Archive ${deal.name}?`, confirmLabel: 'Archive', danger: true });
          if (ok) onSave({ is_archived: true, archived_reason: 'manual' });
        } },
  ];
  return items;
};

// ── the four action drawers DealDetail's classic `overlays` holds (re-mounted here) ─
const DealActionDrawers = ({ deal, company, companyId, stageExits, activeUser, z, d }) => (
  <>
    {d.creatingWorkspace && window.WorkspaceCreatorDrawer && (
      <window.WorkspaceCreatorDrawer deal={deal} activeUser={activeUser} onClose={d.onWorkspaceClose} onCreated={d.onWorkspaceCreated} />
    )}
    {window.ClosingDrawer && (
      <window.ClosingDrawer zIndex={z} open={!!d.wonStage} deal={deal} closing={null} onClose={d.onWonClose} onSubmit={d.onWon} />
    )}
    <window.ExitDealDrawer zIndex={z} open={d.exiting} deal={deal} exits={stageExits} onClose={d.onExitClose} onExit={d.onExit} />
    {window.ReverseAcquisitionDrawer && (
      <window.ReverseAcquisitionDrawer open={d.reversing} companyId={companyId} companyName={deal.targetName || (company && company.name)} zIndex={z}
        onClose={d.onReverseClose} onDone={async () => { d.onReverseClose(); await d.onReverseDone(); }} />
    )}
  </>
);

// ── drawer / workspace bodies registered in record_panels.jsx ─────────────────
// All details: the classic PropertiesPanel, plus Exit notes (exitNotes).
const DealDetailsBody = ({ api }) => {
  const ctx = api.ctx || {};
  const list = ctx.stageList || [];
  if (!api.record) return null;
  return (
    <window.PropertiesPanel deal={api.record} company={ctx.company || null} pipelineStages={list}
      onSave={ctx.saveDeal} onPursuitStage={ctx.savePursuitStage}
      onStageSelect={ctx.requestStage ? (s) => ctx.requestStage(s, list.findIndex((x) => x.id === s.id)) : undefined} exitNotes />
  );
};
// People: search + the ranked list + Add (NewPersonDrawer stacks above the drawer).
const RecordPeopleBody = ({ api }) => {
  const ctx = api.ctx || {};
  const cid = api.facts && api.facts.companyId;
  const own = ctx.contacts !== undefined;                       // the page loaded them (deal: DealPageV2; company: CompanyDetail)
  const [list, setList] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  React.useEffect(() => {
    if (own || !cid || !api.active) return undefined;
    let alive = true;
    window.AtlasApi.listContacts({ company_id: cid, limit: 200 }).then((r) => { if (alive) setList((r && r.items) || []); }).catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [own, cid, api.active]);
  // adapted (company page) or raw (deal page); null while the page's own load is in flight → '—'
  const rows = own ? (ctx.contacts ? ctx.contacts.map((c) => (c && c._raw ? c._raw : c)) : null) : list;
  const primaryId = api.record && api.record._raw ? api.record._raw.primary_contact_id : null;
  const ranked = DealPageKit.rankPeople(rows || [], primaryId);
  const needle = q.trim().toLowerCase();
  const shown = needle ? ranked.filter((p) => `${p.full_name || ''} ${p.first_name || ''} ${p.last_name || ''} ${p.title || ''} ${p.email || ''}`.toLowerCase().includes(needle)) : ranked;
  const companyName = (ctx.company && ctx.company.name) || (api.record && api.record.targetName) || (api.record && api.record.name) || '';
  return (
    <div data-people-body="" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0 10px' }}>
        <window.TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" style={{ flex: 1 }} />
        <span className="num" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{rows ? shown.length : '—'}</span>
        {api.canWrite && window.NewPersonDrawer && cid && (
          <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={() => setAdding(true)}>Add</window.Btn>
        )}
      </div>
      <div style={{ margin: '0 -14px' }}>
        {rows == null ? <div style={{ padding: '8px 14px', ...DP_DIM }}>—</div>
          : shown.length === 0 ? <div style={{ padding: '8px 14px', ...DP_DIM }}>0</div>
          : shown.map((p) => <PersonRow key={p.id} p={p} primary={String(p.id) === String(primaryId || '')} />)}
      </div>
      {adding && window.NewPersonDrawer && (
        <window.NewPersonDrawer zIndex={70} initialCompany={{ id: cid, name: companyName }} onClose={() => setAdding(false)}
          onCreated={(contact) => {   // the company page owns its contacts (onContactCreated prepends + announces); the deal page re-lists
            setAdding(false);
            if (ctx.onContactCreated) ctx.onContactCreated(contact);
            else { if (ctx.reloadContacts) ctx.reloadContacts(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }
          }} />
      )}
    </div>
  );
};
// Files workspace: FilesPanel inside FullScreenPanel (shells, guest deals; step 11 reuses it on companies).
const RecordFilesWorkspace = ({ api }) => {
  const rec = api.rec;
  const scope = rec.kind === 'deal' ? { engagement_id: rec.id } : { company_id: rec.id };
  const name = (api.record && api.record.name) || '';
  return (
    <window.FullScreenPanel active={api.active} title={`${name} › Files`} bodyScroll={false}
      onClose={() => api.close({ via: 'button' })} onEscape={api.escape}>
      <div style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto' }}>
        <window.FilesPanel scope={scope} title="Files" />
      </div>
    </window.FullScreenPanel>
  );
};

// ── the page ──────────────────────────────────────────────────────────────────
const DealPageV2 = (props) => {
  const { deal, company, stageList, currentIdx, stageExits, currentExit, probability, daysInStage, activeUser, guest,
    canOpenCompanies, liveMeeting, isPinned, onTogglePin, back, onSave, onPursuitStage, onRequestStage, onReopen, onReloadDeal,
    tasks, onTasksChange, stageHistory, workspaces, callAnalytics, feed, composeRequest, onCompose, logDraftDot, panel, shared, drawers } = props;
  const id = deal.id;
  const shell = deal.type === 'buyer_buyside';
  const pp = window.usePanelParam({ kind: 'deal', id });                      // z70 for the action drawers over a workspace
  const draftOpen = window.useRecordDraftOpen('deal', id);
  const sum = window.useRecordSummary('deal', id);                            // the one root read (A2): last touch + the acquisition slot; the cards keep their own
  const [users, setUsers] = React.useState([]);
  const [contacts, setContacts] = React.useState(null);
  const [addingContact, setAddingContact] = React.useState(false);
  const [merging, setMerging] = React.useState(false);
  React.useEffect(() => {
    if (guest) return undefined;
    let alive = true;
    window.AtlasApi.listTeamMembers().then((m) => { if (alive) setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []); }).catch(() => { if (alive) setUsers([]); });
    return () => { alive = false; };
  }, [guest]);
  const reloadContacts = React.useCallback(() => {
    if (guest || !deal.target) { setContacts(null); return Promise.resolve(); }
    return window.AtlasApi.listContacts({ company_id: deal.target, limit: 200 }).then((r) => setContacts((r && r.items) || [])).catch(() => setContacts([]));
  }, [guest, deal.target]);
  React.useEffect(() => { reloadContacts(); }, [reloadContacts]);
  React.useEffect(() => {                                                      // a person created or edited elsewhere on this page
    const on = (e) => { if (e && e.detail && e.detail.kind === 'contact') reloadContacts(); };   // only an event that names a contact
    window.addEventListener('atlas-data-changed', on);
    return () => window.removeEventListener('atlas-data-changed', on);
  }, [reloadContacts]);

  const canWrite = !guest && window.recordCanWrite();
  const raw = deal._raw || {};
  // The page's own drawers render in the page tree; over an open reference drawer (z 50, a body portal) or a
  // workspace (z 55) they stack (Z.stacked).
  const z = pp.name ? 70 : undefined;
  const companyId = raw.company_id || deal.target || null;
  const K = window.RecordNotesKit;
  const dup = !!(raw.is_duplicate && raw.merged_into_id);
  const nav = window.AtlasNav || {};
  const s = sum.data || {};
  const acqPending = (s.acquisitions && s.acquisitions.pending) || 0;
  // The acquisition slot only while a row is pending and the pill would not be hidden (A3;
  // the same `hidden` conditions DealDetail passes, minus the shell, which pillKeys excludes).
  const hasAcq = !!shared.acquisitionPill && acqPending > 0 && !deal.exitName && !(company && company._raw && company._raw.is_acquired);

  // ── header
  const typePill = window.ENG_TYPE_PILL[deal.type] ? <window.Pill key="type" hue={window.ENG_TYPE_PILL[deal.type].hue} sz="sm">{window.ENG_TYPE_PILL[deal.type].label}</window.Pill> : null;
  const meetingPill = liveMeeting ? (
    <a key="meeting" href={liveMeeting.meeting_link || undefined} target="_blank" rel="noreferrer" title={liveMeeting.title || 'Meeting'} style={{ textDecoration: 'none' }}>
      <window.Pill hue="green" dot sz="sm">In a meeting now</window.Pill></a>) : null;
  const holdPill = deal.onHold ? <window.Pill key="hold" hue="amber" sz="sm">{DealPageKit.holdLabel(deal, window.HOLD_REASON_LABEL)}</window.Pill> : null;
  const pursuitEl = <PursuitPill deal={deal} canWrite={canWrite} onPursuitStage={onPursuitStage} />;
  const pillOf = { type: typePill, meeting: meetingPill, acquisition: shared.acquisitionPill || null, hold: holdPill, pursuit: <span key="pursuit">{pursuitEl}</span> };
  const pills = DealPageKit.pillKeys(deal, { liveMeeting: !!liveMeeting, hasAcq }).map((k) => pillOf[k]);
  const terminal = DealPageKit.terminal(deal);
  const exitKind = canWrite ? DealPageKit.exitPrimary(deal, { stageExits, currentExit, companyId }) : null;
  const primary = exitKind === 'exit'
    ? <window.Btn key="exit" size="sm" variant="outline" icon={<window.Icon.Close size={12} />} style={{ color: 'var(--rose-fg, oklch(0.50 0.14 25))' }} onClick={drawers.onExitOpen}>Exit deal…</window.Btn>
    : exitKind === 'reopen' ? <window.Btn key="reopen" size="sm" variant="outline" onClick={onReopen}>Reopen deal</window.Btn>
    : exitKind === 'not_acquired' ? <window.Btn key="notacq" size="sm" variant="outline" onClick={drawers.onReverseOpen}>Not acquired…</window.Btn>
    : null;
  const onLog = async (kind) => {
    if (!onCompose) return;
    if (dup) {
      const ok = await window.showConfirm({ title: 'Archived duplicate', body: deal.name, confirmLabel: 'Log here' });
      if (!ok) return;
    }
    onCompose(kind);
  };
  const exitNote = deal.exitName ? (
    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxHeight: 20 }}>
      <window.EditableCell type="multiline" label="Exit notes" value={raw.exit_notes || ''} locked={Boolean(raw.dealcloud_id)} editable={canWrite}
        onSave={(exit_notes) => onSave({ exit_notes: exit_notes || null })} />
    </span>) : null;
  // A shell's seller deal: the pursuit banner's link, and the archived banner's second link.
  const sellerLink = shell && deal.sellerEngagementId
    ? { label: `Open ${deal.sellerEngagementName || 'the deal'}`, onClick: () => nav.goToDeal && nav.goToDeal(deal.sellerEngagementId, { panel: 'buyers', focus: deal.id }) }
    : null;
  // Archived first (the chip's order, DealPageKit.terminal): an archived shell is Archived, not a pursuit.
  const banner = deal.archived ? (
    <BannerLine hue="amber" icon={<window.Icon.Layers size={12} />} text={DealPageKit.bannerText(deal)}
      links={[dup && { label: 'Open the kept deal', onClick: () => nav.goToDeal && nav.goToDeal(raw.merged_into_id) }, sellerLink,
        (company || deal.target) && canOpenCompanies && { label: `Open ${(company && company.name) || 'company'}`, onClick: () => nav.goToCompany && nav.goToCompany((company && company.id) || deal.target) }]} />
  ) : sellerLink && !deal.exitName ? (
    <BannerLine hue="violet" icon={<window.Icon.Users size={12} />} text="Buyer pursuit" links={[sellerLink]} />
  ) : deal.exitName ? (
    <BannerLine hue="rose" icon={<window.Icon.Close size={12} />} text={`${DealPageKit.bannerText(deal)} ·`}>{exitNote}</BannerLine>
  ) : null;
  const band = !shell && stageList.length > 0 ? (
    <window.StageRail stages={stageList} currentIdx={currentIdx} exited={Boolean(deal.exitName)} onSelect={canWrite ? onRequestStage : null}
      history={stageHistory} raw={raw}
      currentLabel={[probability != null ? `${probability}%` : null, daysInStage != null ? `${daysInStage}d` : null].filter(Boolean).map((x) => `· ${x}`).join(' ') || null}
      onCurrentLabel={() => window.openRecordPanel('history', { tab: 'stages' }, { via: 'button' })} />
  ) : null;
  const terminalEl = terminal ? <window.Pill hue={terminal === 'Exited' ? 'rose' : 'amber'} sz="sm">{terminal}</window.Pill> : null;
  const stageEl = shell ? pursuitEl
    : stageList.length ? <DealStagePill deal={deal} stageList={stageList} canWrite={canWrite} onRequestStage={onRequestStage} /> : null;
  const titleEl = guest || !canWrite ? <h1 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{deal.name}</h1>
    : <window.EditableTitle value={deal.name} onSave={(name) => onSave({ name })} />;
  const header = {
    crumb: { label: guest ? 'Your deals' : 'Engagements', onClick: back },
    logo: <window.Logo name={(company && company.name) || deal.name} color={company && company.logo} size={32} />,
    title: titleEl,
    pills,
    terminal: terminalEl,
    stage: stageEl,
    primaries: primary ? [primary] : [],
    log: <window.LogButton onPick={onLog} disabled={!activeUser} dot={logDraftDot} />,
    menu: <window.RecordMenu items={dealMenuItems({ deal, shell, isPinned, onTogglePin, drawers, onSave, setMerging })} />,
    meta: <DealMetaLine deal={deal} company={company} canOpenCompanies={canOpenCompanies} />,
    lastTouch: sum.loading ? undefined : (s.last_touch || null),             // hidden while loading, dimmed '—' once loaded with none (A2)
    banner, band,
  };

  // ── composer (RecordPage's pane; openers go through onCompose → RecordNotes)
  const attach = { engagement_id: deal.id, company_id: deal.target || null };
  const onSaved = (r) => {
    if (r.type === 'task') { if (onTasksChange) onTasksChange(); window.dispatchEvent(new CustomEvent('task-created')); return; }
    if (feed.reload) feed.reload();
    if (r.updatesDeal && onReloadDeal) onReloadDeal();
    if (r.updatesDeal && onTasksChange) onTasksChange();   // a Next action note syncs the deal's Next Action task server-side
  };
  const composer = {
    open: draftOpen && canWrite,
    element: canWrite ? (
      <window.RecordComposer rec={{ kind: 'deal', id }} attach={attach} deal={deal} activeUser={activeUser} members={users}
        kinds={K.composerKindsFor(true)} presentation="inline" onSaved={onSaved} />) : null,
    title: 'Log',
  };

  // ── columns
  const left = (
    <>
      <DealKeyFacts deal={deal} company={company} stageList={stageList} users={users} canWrite={canWrite} guest={guest} onSave={onSave} shared={shared} />
      <PeopleCard deal={deal} guest={guest} canWrite={canWrite} contacts={contacts} onAdd={() => setAddingContact(true)} />
    </>
  );
  // The openers (Update, + Task, + Add note, the feed drop) gate on the same canWrite as the
  // pane: a viewer (not a guest, recordCanWrite() false) would otherwise open a draft nothing
  // renders. RecordNotes gates its own affordances on activeUser, so a viewer gets none.
  const act = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {!shell && window.ClosingSection && <window.ClosingSection deal={deal} />}
      {!shell && !guest && window.CloseTimelineCard && <window.CloseTimelineCard deal={deal} canWrite={canWrite} onSave={onSave} />}
      <window.NextActionCard deal={deal} onSave={onSave} onCompose={canWrite && onCompose ? onCompose : null} />
      {/* Open tasks right under Next action, shown at 0 for a writer (DealDetail's `tasks`: 50 rows, both statuses): views/record_tasks.jsx */}
      {window.RecordTasks && (
        <window.RecordTasks kind="deal" presentation="card" deal={deal} tasks={tasks} users={users} activeUser={activeUser}
          canWrite={canWrite} onTasksChange={onTasksChange} onCompose={canWrite && onCompose ? onCompose : null} />
      )}
      {!shell && !guest && deal.target && window.CompanySignalsSection && (
        <window.CompanySignalsSection companyId={deal.target} sectionId="deal.call_triggers" marketHere />
      )}
    </div>
  );
  const feedEl = (
    <window.RecordNotes kind="deal" recordId={deal.id} sectionId="deal.notes" composerSlot="external"
      feed={feed} attach={attach} deal={deal} hideCompanyId={deal.target} activeUser={canWrite ? activeUser : null} members={users}
      talkTime={window.talkTimeByNote(callAnalytics)} onDealChange={onReloadDeal} onTasksChange={onTasksChange}
      composeRequest={composeRequest} />
  );
  const variant = shell ? 'shell' : 'seller';
  const cardIds = DealPageKit.cardIds(variant, { guest, noCompany: !deal.target });
  const openPanel = (name, params) => () => window.openRecordPanel(name, params || {}, { via: 'button' });
  const rails = {
    left: [{ key: 'key_facts', icon: <window.Icon.List size={13} />, label: 'Key facts', onOpen: openPanel('details') },
      !guest && deal.target && { key: 'people', icon: <window.Icon.Users size={13} />, label: 'People', onOpen: openPanel('people') }],
    right: shell
      ? [{ key: 'seller_deal', icon: <window.Icon.Pipeline size={13} />, label: 'Seller deal', onOpen: () => deal.sellerEngagementId && nav.goToDeal && nav.goToDeal(deal.sellerEngagementId, { panel: 'buyers', focus: deal.id }) },
         { key: 'meetings', icon: <window.Icon.Phone size={13} />, label: 'Meetings & calls', onOpen: openPanel('calls') },
         { key: 'files', icon: <window.Icon.Folder size={13} />, label: 'Files', onOpen: openPanel('files') }]
      : [{ key: 'buyers', icon: <window.Icon.Users size={13} />, label: 'Buyers', onOpen: openPanel('buyers') },
         { key: 'intel', icon: <window.Icon.Building size={13} />, label: 'Company intel', onOpen: openPanel('intel') },
         !guest && { key: 'market', icon: <window.Icon.ChartBar size={13} />, label: 'Market', onOpen: openPanel('market') },
         !guest && deal.target && { key: 'vault', icon: <window.Icon.Folder size={13} />, label: 'Data vault', onOpen: openPanel('vault') },
         guest && { key: 'files', icon: <window.Icon.Folder size={13} />, label: 'Files', onOpen: openPanel('files') },
         { key: 'meetings', icon: <window.Icon.Phone size={13} />, label: 'Meetings & calls', onOpen: openPanel('calls') },
         !guest && { key: 'workspaces', icon: <window.Icon.Sparkle size={13} />, label: 'Agent workspaces', onOpen: () => (workspaces && workspaces[0] && nav.goToWorkspace) && nav.goToWorkspace(workspaces[0].id) }],
  };
  const panelProp = {
    record: deal, facts: panel.facts, variant,
    ctx: { ...panel.ctx, saveDeal: onSave, savePursuitStage: onPursuitStage, activeUser, tasks, onTasksChange, stageHistory, users,
      contacts, reloadContacts, callAnalytics, onReverseOpen: drawers.onReverseOpen },
  };
  return (
    <>
      <window.RecordPage kind="deal" id={id} header={header} left={left} act={act} feed={feedEl}
        right={<DealRight deal={deal} variant={variant} guest={guest} />} cardIds={cardIds} rails={rails}
        composer={composer} panel={panelProp} onNoteChange={feed.reload} />
      <DealActionDrawers deal={deal} company={company} companyId={companyId} stageExits={stageExits} activeUser={activeUser} z={z} d={drawers} />
      {merging && <DealMergeDrawer deal={deal} onClose={() => setMerging(false)} />}
      {addingContact && window.NewPersonDrawer && deal.target && (
        <window.NewPersonDrawer initialCompany={{ id: deal.target, name: (company && company.name) || deal.targetName || '' }} zIndex={z}
          onClose={() => setAddingContact(false)}
          onCreated={() => { setAddingContact(false); reloadContacts(); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
      )}
    </>
  );
};

Object.assign(window, { DealPageV2, DealDetailsBody, RecordPeopleBody, RecordFilesWorkspace });
